/* ==========================================================================
   THEME: Impact v7.0.1 merge target + Gro unified rebuild
   STORES: Gro Clinics AU (groclinics.com.au) + NZ (groclinics.co.nz)
   LAST UPDATED: 25/08/2026
   PREVIOUSLY: 01/07/2026 (sparse-doctor fill reverted; per-doctor SEO
     subheading added on the Liquid side)
   ==========================================================================
   Impact base-theme reference: the vendor URL in assets/documentation.txt.
   Gro design spec: 05-design/00-web-design-system (vault root relative).
   Custom Gro modules are documented in snippets/_gro-docs-*.liquid.
   ========================================================================== */

/**
 * gro-results.js
 * Location: assets/gro-results.js
 *
 * IDENTITY
 *   The entire client-side runtime for the Gro before-and-after results
 *   module. It reads two JSON data islands the section prints, then builds,
 *   filters, sorts, paginates and lightboxes every result card.
 *
 * OWNERSHIP
 *   OWNS   card markup, the filter state machine, pagination, the mobile
 *          and desktop limit logic, the results count, and the lightbox
 *          (which it appends to <body>, outside the section).
 *   DOES NOT OWN  any colour, and any styling beyond two small inline
 *          leftovers noted under KNOWN DEBT. Class names come from
 *          assets/gro-results.css; colours come from the CSS custom
 *          properties the section declares. It also does not own the
 *          AHPRA-mandated disclaimer text, which is regulatory content.
 *
 * GRO MODULE CONTRACT - read before editing
 *   1. #groResultsContainer arrives EMPTY. Everything visible in the grid
 *      is produced here. If either JSON island fails to parse, the script
 *      logs and falls back to an empty array or to the built-in defaults,
 *      so the page degrades to a heading with no grid rather than throwing.
 *   2. THE DATA CONTRACT IS BY KEY NAME. Every key read from
 *      #groResultsData and #groSectionSettings is written verbatim by
 *      sections/gro-results.liquid. Renaming a key on one side only fails
 *      silently, with no console error and no visual clue beyond a missing
 *      value. Change both files in the same commit, always.
 *   3. EVERY CARD MUST CARRY A TREATMENT DISCLAIMER. Priority is
 *      result.treatmentDisclaimer, then settings.cardDisclaimer, then the
 *      TREATMENT_DISCLAIMER constant below. That third tier is the last
 *      resort that guarantees a card is never published without one. Do
 *      not remove it, do not make the disclaimer conditional, and do not
 *      move it behind a toggle. The registration label under each doctor
 *      name is the same class of regulatory content and is built in
 *      Liquid, not here.
 *   4. Class names used in the innerHTML strings are styled by
 *      gro-results.css. Renaming one here without renaming it there
 *      silently unstyles that element.
 *   5. The elements #groFilterBar and #groResultsCount are queried
 *      unconditionally. The section emits hidden placeholders for both when
 *      filters are off, precisely so this script does not need a guard.
 *      Removing those placeholders breaks the script.
 *   6. The mobile breakpoint is duplicated. isMobile() is
 *      window.innerWidth <= 580 here, and gro-results.css and the section's
 *      inline style both branch at 580px. Change one and you must change
 *      all three.
 *
 * DATA ISLANDS
 *   #groResultsData     - array of result objects, one per hair_result entry
 *   #groSectionSettings - configuration object built from section settings
 *   See: sections/gro-results.liquid
 *
 * DEPENDENCIES
 *   sections/gro-results.liquid prints both data islands and loads this file
 *   deferred. assets/gro-results.css supplies every class name. No library,
 *   no framework, no app, no global event bus. Two globals are published on
 *   window on purpose, because they are called from inline onclick
 *   attributes in generated markup: groRemoveFilter and groChangePage.
 *
 * KNOWN DEBT - documented 25/08/2026, not changed here
 *   Two inline styles survive in generated markup: the empty doctor-filter
 *   message carries `font-size: 14px; color: #666`, and the mobile
 *   pagination counter carries `padding: 0 12px; font-size: 14px`. Both
 *   belong in gro-results.css. Removal trigger: next material edit to
 *   either function.
 *   Card markup is assembled by string concatenation into innerHTML, and
 *   the filter-pill remove button uses an inline onclick calling a window
 *   global. Both are pre-existing architecture, not new. Removal trigger:
 *   a deliberate rewrite, not a documentation pass.
 *
 * DELIBERATE EXEMPTIONS
 *   AGENTS.md "CSS over JS" does not make this file an exception to fix:
 *   the card set is serialised as DATA, not rendered as markup, so building
 *   the grid genuinely requires JavaScript. The one resize listener is
 *   debounced at 120ms and re-renders only when the mobile boundary is
 *   actually crossed, because the visible card LIMIT differs between mobile
 *   and desktop and no CSS rule can recalculate a count. No other
 *   exemptions.
 */

(function () {
  // --- Constants ---
  const ARROW_SVG =
    'data:image/svg+xml,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="24" height="24"><path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z"/></svg>',
    );

  const TREATMENT_DISCLAIMER =
    'Results are specific to this patient and may not reflect outcomes others may experience. ' +
    'Individual results vary. Hair transplant procedures carry risk. ' +
    'Book a consultation to discuss your suitability and what to expect.';

  // --- Parse JSON safely ---
  let allResults = [];
  const settings = {
    featuredFirst: true,
    enableFilters: false,
    enablePagination: false,
    perPageDesktop: 9,
    filterByDoctor: false,
    selectedDoctors: '',
    filterByLocation: false,
    selectedLocations: '',
    showButton: true,
    showLightbox: true,
    enablePairing: true,
    resultsLimitMobile: 9,
    resultsLimitDesktop: 9,
    cardDisclaimer: null,
  };

  try {
    allResults = JSON.parse(
      document.getElementById('groResultsData').textContent,
    );
  } catch (e) {
    console.error('[groResultsData] invalid JSON', e);
    allResults = [];
  }

  try {
    const parsed = JSON.parse(
      document.getElementById('groSectionSettings').textContent,
    );
    for (const key in parsed) {
      if (parsed.hasOwnProperty(key) && parsed[key] !== null) settings[key] = parsed[key];
    }
  } catch (e) {
    console.error('[groSectionSettings] invalid JSON', e);
  }

  // --- Pre-filters (applied before user interaction) ---

  if (settings.featuredFirst) {
    allResults.sort((a, b) => {
      const aFeat = a.featured === true || a.featured === 'true' ? 0 : 1;
      const bFeat = b.featured === true || b.featured === 'true' ? 0 : 1;
      return aFeat - bFeat;
    });
  }

  let isDoctorFiltered = false;
  if (settings.filterByDoctor && settings.selectedDoctors) {
    const selectedDoctorsList = settings.selectedDoctors
      .split(',')
      .map(name => name.trim())
      .filter(name => name.length > 0);

    if (selectedDoctorsList.length > 0) {
      const doctorMatches = allResults.filter(result => {
        if (!result.doctor || !Array.isArray(result.doctor) || result.doctor.length === 0)
          return false;
        return result.doctor.some(doc =>
          selectedDoctorsList.some(
            selected => doc.doctorName && doc.doctorName.trim() === selected.trim()
          )
        );
      });

      if (doctorMatches.length > 0) {
        // Results exist for this doctor - apply the filter
        isDoctorFiltered = true;
        allResults = doctorMatches;
      } else if (!settings.autoFilterFallbackAll) {
        // Manual filter with no matches - honour it and show empty state
        isDoctorFiltered = true;
        allResults = doctorMatches;
      }
      // autoFilterFallbackAll + 0 matches: leave allResults intact (show everything)
    }
  }

  // --- State ---
  const isMobile = () => window.innerWidth <= 580;
  const resultsPerPage = () => settings.perPageDesktop;

  let currentPage = 1;
  let filteredResults = allResults.slice();
  const activeFilters = { age: [], condition: [], location: [], doctor: [] };

  // --- Elements ---
  const container = document.getElementById('groResultsContainer');
  const paginationContainer = document.getElementById('groPagination');
  const ctaContainer = document.getElementById('groCtaContainer');
  const filterBar = document.getElementById('groFilterBar');
  const filterPanel = document.getElementById('groFilterPanel');
  const filterOverlay = document.getElementById('groFilterOverlay');
  const filterToggleBtn = document.getElementById('groFilterToggleBtn');
  const closePanelBtn = document.getElementById('groClosePanelBtn');
  const applyFiltersBtn = document.getElementById('groApplyFiltersBtn');
  const clearAllFiltersBtn = document.getElementById('groClearAllBtn');
  const clearFiltersBtn = document.getElementById('groClearFiltersBtn');
  const activeFiltersContainer = document.getElementById('groActiveFilters');
  const resultsCountEl = document.getElementById('groResultsCount');

  // --- Helpers ---
  const isTrue = v => v === true || v === 'true' || v === 1 || v === '1';
  const toSlug = s => (s || '').toString().toLowerCase().trim().replace(/\s+/g, '-');

  if (!settings.enableFilters && filterBar) filterBar.style.display = 'none';
  if (isDoctorFiltered && filterBar) filterBar.classList.add('doctor-filtered');

  // Location pre-filter
  let isLocationFiltered = false;
  if (settings.filterByLocation && settings.selectedLocations) {
    const selectedLocationsList = settings.selectedLocations
      .split(',')
      .map(name => name.trim().toLowerCase())
      .filter(name => name.length > 0);

    if (selectedLocationsList.length > 0) {
      isLocationFiltered = true;
      allResults = allResults.filter(result => {
        const loc = (result.location || '').toString().toLowerCase().trim();
        const locSlug = toSlug(result.location_slug || result.location || '');
        return selectedLocationsList.some(selected => selected === loc || selected === locSlug);
      });
      filteredResults = allResults.slice();
    }
  }

  // --- Dynamic doctor filters ---
  function generateDoctorFilters() {
    const doctorFiltersContainer = document.getElementById('groDoctorFilters');
    if (!doctorFiltersContainer) return;

    const doctorSet = new Set();
    allResults.forEach(result => {
      if (result.doctor && Array.isArray(result.doctor)) {
        result.doctor.forEach(doc => {
          if (doc.doctorName) doctorSet.add(doc.doctorName);
        });
      }
    });

    const doctorArray = Array.from(doctorSet).sort();
    if (doctorArray.length === 0) {
      doctorFiltersContainer.innerHTML = '<p style="font-size: 14px; color: #666;">No doctors available</p>';
      return;
    }

    doctorFiltersContainer.innerHTML = doctorArray
      .map(name => {
        const id = 'doctor-' + toSlug(name);
        return (
          '<div class="gro-filter-option">' +
          '<input type="checkbox" id="' + id + '" value="' + name + '" class="doctor-filter">' +
          '<label for="' + id + '">' + name + '</label>' +
          '</div>'
        );
      })
      .join('');
  }

  // --- Filter panel behaviour ---
  if (filterToggleBtn && filterPanel && filterOverlay) {
    filterToggleBtn.addEventListener('click', () => {
      filterPanel.classList.add('active');
      filterOverlay.classList.add('active');
    });
    closePanelBtn.addEventListener('click', closeFilterPanel);
    filterOverlay.addEventListener('click', closeFilterPanel);
  }

  function closeFilterPanel() {
    if (filterPanel) filterPanel.classList.remove('active');
    if (filterOverlay) filterOverlay.classList.remove('active');
  }

  if (applyFiltersBtn) {
    applyFiltersBtn.addEventListener('click', () => {
      try {
        collectFilters();
        applyFilters();
      } catch (e) {
        console.error('[filters] apply failed', e);
        filteredResults = [];
        render();
      } finally {
        closeFilterPanel();
      }
    });
  }

  if (clearAllFiltersBtn) {
    clearAllFiltersBtn.addEventListener('click', () => {
      document
        .querySelectorAll('.age-filter, .condition-filter, .location-filter, .doctor-filter')
        .forEach(cb => { cb.checked = false; });
      activeFilters.age = [];
      activeFilters.condition = [];
      activeFilters.location = [];
      activeFilters.doctor = [];
      applyFilters();
    });
  }

  if (clearFiltersBtn) {
    clearFiltersBtn.addEventListener('click', () => {
      activeFilters.age = [];
      activeFilters.condition = [];
      activeFilters.location = [];
      activeFilters.doctor = [];
      document
        .querySelectorAll('.age-filter, .condition-filter, .location-filter, .doctor-filter')
        .forEach(cb => { cb.checked = false; });
      applyFilters();
    });
  }

  // --- Collect filters ---
  function collectFilters() {
    activeFilters.age = Array.from(document.querySelectorAll('.age-filter:checked')).map(cb => cb.value);
    activeFilters.condition = Array.from(document.querySelectorAll('.condition-filter:checked')).map(cb => cb.value);
    activeFilters.location = Array.from(document.querySelectorAll('.location-filter:checked')).map(cb => cb.value.toLowerCase().trim());
    activeFilters.doctor = Array.from(document.querySelectorAll('.doctor-filter:checked')).map(cb => cb.value);
  }

  // --- Apply filter logic ---
  function applyFilters() {
    try {
      filteredResults = allResults.filter(result => {
        if (activeFilters.age.length > 0) {
          const age = Number(result.age);
          if (!Number.isFinite(age)) return false;
          const ageMatch = activeFilters.age.some(range => {
            if (range === '20s') return age >= 20 && age <= 29;
            if (range === '30s') return age >= 30 && age <= 39;
            if (range === '40s') return age >= 40 && age <= 49;
            if (range === '50+') return age >= 50;
            return false;
          });
          if (!ageMatch) return false;
        }
        if (activeFilters.condition.length > 0) {
          const hasCondition = activeFilters.condition.some(c => {
            if (c === 'receding') return isTrue(result.receding);
            if (c === 'thinning') return isTrue(result.thinning);
            if (c === 'balding') return isTrue(result.balding);
            return false;
          });
          if (!hasCondition) return false;
        }
        if (activeFilters.location.length > 0) {
          const rSlug = toSlug(result.location_slug || result.location || '');
          const rText = (result.location || '').toString().toLowerCase().trim();
          const hasLocation = activeFilters.location.some(loc => loc === rSlug || loc === rText);
          if (!hasLocation) return false;
        }
        if (activeFilters.doctor.length > 0) {
          if (!result.doctor || !Array.isArray(result.doctor) || result.doctor.length === 0)
            return false;
          const hasDoctor = result.doctor.some(doc => activeFilters.doctor.includes(doc.doctorName));
          if (!hasDoctor) return false;
        }
        return true;
      });
    } catch (e) {
      console.error('[filters] error', e);
      filteredResults = [];
    }
    currentPage = 1;
    render();
  }

  // --- Filter pills ---
  function updateFilterPills() {
    if (!activeFiltersContainer || !clearFiltersBtn) return;
    activeFiltersContainer.innerHTML = '';

    const allActive = [
      ...activeFilters.age.map(f => ({
        type: 'age',
        value: f,
        label: f === '20s' ? "20's" : f === '30s' ? "30's" : f === '40s' ? "40's" : f,
      })),
      ...activeFilters.condition.map(f => ({
        type: 'condition',
        value: f,
        label: f.charAt(0).toUpperCase() + f.slice(1),
      })),
      ...activeFilters.location.map(f => ({
        type: 'location',
        value: f,
        label: f.charAt(0).toUpperCase() + f.slice(1),
      })),
      ...activeFilters.doctor.map(f => ({ type: 'doctor', value: f, label: f })),
    ];

    allActive.forEach(filter => {
      const pill = document.createElement('div');
      pill.className = 'gro-filter-pill';
      pill.innerHTML =
        '<span>' + filter.label + '</span>' +
        '<button onclick="groRemoveFilter(\'' + filter.type + '\', \'' + filter.value.replace(/'/g, "\\'") + '\')">&#215;</button>';
      activeFiltersContainer.appendChild(pill);
    });

    clearFiltersBtn.style.display = allActive.length > 0 ? 'block' : 'none';
  }

  window.groRemoveFilter = (type, value) => {
    activeFilters[type] = activeFilters[type].filter(f => f !== value);
    const checkbox = document.querySelector('.' + type + '-filter[value="' + value + '"]');
    if (checkbox) checkbox.checked = false;
    applyFilters();
  };

  // --- Results count ---
  function updateResultsCount() {
    if (!resultsCountEl) return;
    if (!settings.enablePagination && !settings.enableFilters) {
      resultsCountEl.style.display = 'none';
      return;
    }
    const perPage = resultsPerPage();
    const start = (currentPage - 1) * perPage + 1;
    const end = Math.min(currentPage * perPage, filteredResults.length);
    const total = filteredResults.length;
    resultsCountEl.textContent =
      'Showing ' + (total ? start : 0) + '-' + (total ? end : 0) + ' of ' + total + ' results';
  }

  // --- Card renderer ---
  function renderCard(result) {
    const bLarge = result.beforeImageLarge || result.beforeImage || '';
    const aLarge = result.afterImageLarge || result.afterImage || '';

    // BEFORE / AFTER labels - always visible on images
    const afterLabel = result.duration
      ? 'AFTER &middot; ' + result.duration
      : 'AFTER';

    function imgSlot(src, largeSrc, smallSrc, labelHtml, startIndex) {
      const label = '<span class="gro-img-label">' + labelHtml + '</span>';
      if (!src) return '<div class="gro-img-slot"><div class="gro-placeholder-image"></div></div>';
      if (settings.showLightbox) {
        return (
          '<div class="gro-img-slot">' +
          label +
          '<button class="gro-lightbox-trigger"' +
          ' data-before="' + bLarge + '"' +
          ' data-after="' + aLarge + '"' +
          ' data-small-before="' + result.beforeImage + '"' +
          ' data-small-after="' + result.afterImage + '"' +
          ' data-start="' + startIndex + '"' +
          ' aria-label="View image larger">' +
          '<img src="' + src + '" alt="" loading="lazy">' +
          '</button>' +
          '</div>'
        );
      }
      return (
        '<div class="gro-img-slot">' +
        label +
        '<img src="' + src + '" alt="" loading="lazy">' +
        '</div>'
      );
    }

    const beforeSlot = imgSlot(result.beforeImage, bLarge, result.beforeImage, 'BEFORE', 0);
    const afterSlot  = imgSlot(result.afterImage,  aLarge, result.afterImage,  afterLabel, 1);

    const imagesClass = settings.enablePairing ? 'gro-result-images' : 'gro-result-images gro-result-images--stacked';

    let detailsHtml = '';
    const hasDoctor = result.doctor && result.doctor.length > 0;
    const hasCustomTag = result.customTag1;

    if (hasDoctor || hasCustomTag) {
      let detailsInner = '';

      if (hasDoctor) {
        const treatmentDisclaimerHtml = result.treatmentDisclaimer
          ? '<p class="gro-detail-disclaimer">' + result.treatmentDisclaimer + '</p>'
          : settings.cardDisclaimer
            ? settings.cardDisclaimer
            : '<p class="gro-detail-disclaimer">' + TREATMENT_DISCLAIMER + '</p>';

        result.doctor.forEach(doctor => {
          detailsInner +=
            '<div class="gro-details-inner">' +
            '<span class="gro-preforming-doctor">Performed by ' + (doctor.doctorName || '') + '</span>' +
            (doctor.registrationLabel
              ? '<p class="gro-reg-number">' + doctor.registrationLabel + '</p>'
              : '') +
            (hasCustomTag
              ? '<p class="gro-detail-note">' + result.customTag1 + '</p>'
              : '') +
            treatmentDisclaimerHtml +
            '</div>';
        });
      } else if (hasCustomTag) {
        detailsInner +=
          '<div class="gro-details-inner">' +
          '<p class="gro-detail-note">' + result.customTag1 + '</p>' +
          '</div>';
      }

      detailsHtml =
        '<div class="gro-details-dropdown">' +
        '<button class="gro-details-toggle" onclick="this.classList.toggle(\'active\'); this.nextElementSibling.classList.toggle(\'active\');">' +
        '<span>Details</span>' +
        '<img src="' + ARROW_SVG + '" alt="" class="gro-details-arrow">' +
        '</button>' +
        '<div class="gro-details-content">' +
        detailsInner +
        '</div>' +
        '</div>';
    }

    let tags = '';
    if (result.age) tags += '<span>Age ' + result.age + '</span>';
    if (result.receding) tags += '<span>Receding</span>';
    if (result.thinning) tags += '<span>Thinning</span>';
    if (result.balding) tags += '<span>Balding</span>';
    if (result.showHairTransplant) tags += '<span>Hair Transplant</span>';
    if (result.showPHC) tags += '<span>PHC</span>';
    if (result.customTag2) tags += '<span>' + result.customTag2 + '</span>';
    if (result.duration) tags += '<span>' + result.duration + '</span>';

    return (
      '<div class="gro-result-card">' +
      '<div class="' + imagesClass + '">' + beforeSlot + afterSlot + '</div>' +
      '<div class="gro-result-info">' +
      detailsHtml +
      '<div class="gro-tags">' + tags + '</div>' +
      '</div>' +
      '</div>'
    );
  }

  // Re-render on breakpoint change so the limit recalculates for mobile vs desktop
  let wasMobile = isMobile();
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const nowMobile = isMobile();
      if (nowMobile !== wasMobile) {
        wasMobile = nowMobile;
        render();
      }
    }, 120);
  }, { passive: true });

  // --- Results renderer ---
  function renderResults() {
    if (!container) return;
    if (!Array.isArray(filteredResults)) filteredResults = [];

    const perPage = resultsPerPage();
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    let pageResults = settings.enablePagination
      ? filteredResults.slice(start, end)
      : filteredResults;

    // Apply hard limit when pagination is off - CTA handles full browsing
    if (!settings.enablePagination) {
      const limit = isMobile()
        ? (parseInt(settings.resultsLimitMobile, 10) || 9)
        : (parseInt(settings.resultsLimitDesktop, 10) || 9);
      pageResults = pageResults.slice(0, limit);
    }

    if (pageResults.length === 0) {
      container.innerHTML =
        '<div class="gro-no-results">' +
        '<h3>No results found</h3>' +
        '<p>Try adjusting your filters to see more results</p>' +
        (settings.enableFilters
          ? '<button class="button gro-btn-primary" onclick="document.getElementById(\'groClearFiltersBtn\').click()">Clear All Filters</button>'
          : '') +
        '</div>';
      return;
    }

    let html;
    if (isMobile()) {
      // Mobile: 3 horizontal rows, each with independent scroll
      const rows = [[], [], []];
      pageResults.forEach((r, i) => rows[i % 3].push(r));

      html = '<div class="gro-mobile-rows">';
      rows.forEach(row => {
        html += '<div class="gro-mobile-row">';
        row.forEach(result => { html += renderCard(result); });
        html += '</div>';
      });
      html += '</div>';
    } else {
      // Desktop: standard 3-column responsive grid
      html = '<div class="gro-results-grid">';
      pageResults.forEach(result => { html += renderCard(result); });
      html += '</div>';
    }
    container.innerHTML = html;
  }

  // --- Pagination renderer ---
  function renderPagination() {
    if (!paginationContainer) return;
    if (!settings.enablePagination) {
      paginationContainer.innerHTML = '';
      return;
    }

    const perPage = resultsPerPage();
    const totalPages = Math.ceil(filteredResults.length / perPage);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      if (ctaContainer) ctaContainer.style.display = '';
      return;
    }

    if (ctaContainer) ctaContainer.style.display = 'none';

    const mobile = isMobile();
    let html =
      '<button class="gro-pagination-btn" onclick="groChangePage(' + (currentPage - 1) + ')"' +
      (currentPage === 1 ? ' disabled' : '') +
      '>&larr;</button>';

    if (mobile && totalPages > 5) {
      html += '<span style="padding: 0 12px; font-size: 14px;">' + currentPage + ' / ' + totalPages + '</span>';
    } else {
      const maxVisible = mobile ? 3 : 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let endPage = Math.min(totalPages, startPage + maxVisible - 1);
      if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1);
      for (let i = startPage; i <= endPage; i++) {
        html +=
          '<button class="gro-pagination-number' + (i === currentPage ? ' active' : '') +
          '" onclick="groChangePage(' + i + ')">' + i + '</button>';
      }
    }

    html +=
      '<button class="gro-pagination-btn" onclick="groChangePage(' + (currentPage + 1) + ')"' +
      (currentPage === totalPages ? ' disabled' : '') +
      '>&rarr;</button>';
    paginationContainer.innerHTML = html;
  }

  window.groChangePage = page => {
    const perPage = resultsPerPage();
    const totalPages = Math.ceil(filteredResults.length / perPage);
    if (page < 1 || page > totalPages) return;
    currentPage = page;
    render();
    const anchor = document.getElementById('groResults');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // --- Master render ---
  function render() {
    renderResults();
    renderPagination();
    if (settings.enableFilters) updateFilterPills();
    updateResultsCount();
  }

  // --- Lightbox ---
  let lbImages = [];
  let lbIndex = 0;

  function initLightbox() {
    if (!settings.showLightbox) return;
    if (document.getElementById('groLightbox')) return;

    const lb = document.createElement('div');
    lb.id = 'groLightbox';
    lb.className = 'gro-lightbox';
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML =
      '<div class="gro-lightbox-backdrop"></div>' +
      '<button class="gro-lightbox-close" aria-label="Close">&times;</button>' +
      '<div class="gro-lightbox-inner">' +
        '<div class="gro-lightbox-desktop">' +
          '<div class="gro-lightbox-slot">' +
            '<img class="gro-lb-before" src="" alt="Before">' +
            '<span class="gro-lightbox-label">BEFORE</span>' +
          '</div>' +
          '<div class="gro-lightbox-slot">' +
            '<img class="gro-lb-after" src="" alt="After">' +
            '<span class="gro-lightbox-label">AFTER</span>' +
          '</div>' +
        '</div>' +
        '<div class="gro-lightbox-mobile">' +
          '<button class="gro-lightbox-prev" aria-label="Previous">&#8249;</button>' +
          '<div class="gro-lightbox-mobile-wrap">' +
            '<img class="gro-lb-single" src="" alt="">' +
            '<span class="gro-lb-label-mobile"></span>' +
          '</div>' +
          '<button class="gro-lightbox-next" aria-label="Next">&#8250;</button>' +
        '</div>' +
        '<div class="gro-lightbox-dots">' +
          '<span class="gro-lightbox-dot"></span>' +
          '<span class="gro-lightbox-dot"></span>' +
        '</div>' +
      '</div>';
    document.body.appendChild(lb);

    lb.querySelector('.gro-lightbox-backdrop').addEventListener('click', closeLightbox);
    lb.querySelector('.gro-lightbox-close').addEventListener('click', closeLightbox);
    lb.querySelector('.gro-lightbox-prev').addEventListener('click', () => navigateLightbox(-1));
    lb.querySelector('.gro-lightbox-next').addEventListener('click', () => navigateLightbox(1));

    let touchStartX = 0;
    const mobileWrap = lb.querySelector('.gro-lightbox-mobile-wrap');
    mobileWrap.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    mobileWrap.addEventListener('touchend', e => {
      const delta = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(delta) > 50) navigateLightbox(delta < 0 ? 1 : -1);
    });

    document.addEventListener('keydown', e => {
      const el = document.getElementById('groLightbox');
      if (!el || !el.classList.contains('active')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    });
  }

  function setLbImage(el, image) {
    if (!el || !image) return;
    el.src = image.smallSrc || image.src;
    if (image.src && image.src !== image.smallSrc) {
      const loader = new Image();
      loader.onload = () => { el.src = image.src; };
      loader.src = image.src;
    }
  }

  function openLightbox(images, startIndex) {
    lbImages = images;
    lbIndex = startIndex || 0;
    const lb = document.getElementById('groLightbox');
    if (!lb) return;
    setLbImage(lb.querySelector('.gro-lb-before'), images[0]);
    setLbImage(lb.querySelector('.gro-lb-after'), images[1]);
    updateLightboxMobile();
    lb.classList.add('active');
    lb.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    const lb = document.getElementById('groLightbox');
    if (!lb) return;
    lb.classList.remove('active');
    lb.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function navigateLightbox(dir) {
    lbIndex = Math.max(0, Math.min(lbImages.length - 1, lbIndex + dir));
    updateLightboxMobile();
  }

  function updateLightboxMobile() {
    const lb = document.getElementById('groLightbox');
    if (!lb) return;
    const current = lbImages[lbIndex];
    if (!current) return;
    const img = lb.querySelector('.gro-lb-single');
    const label = lb.querySelector('.gro-lb-label-mobile');
    if (img) setLbImage(img, current);
    if (label) label.textContent = current.label;
    lb.querySelectorAll('.gro-lightbox-dot').forEach((dot, i) => dot.classList.toggle('active', i === lbIndex));
    const prev = lb.querySelector('.gro-lightbox-prev');
    const next = lb.querySelector('.gro-lightbox-next');
    if (prev) prev.style.opacity = lbIndex === 0 ? '0.3' : '1';
    if (next) next.style.opacity = lbIndex === lbImages.length - 1 ? '0.3' : '1';
  }

  if (container) {
    const preloadTrigger = trigger => {
      if (!trigger || trigger._preloaded) return;
      trigger._preloaded = true;
      if (trigger.dataset.before) new Image().src = trigger.dataset.before;
      if (trigger.dataset.after) new Image().src = trigger.dataset.after;
    };
    container.addEventListener('mouseover', e => preloadTrigger(e.target.closest('.gro-lightbox-trigger')), { passive: true });
    container.addEventListener('touchstart', e => preloadTrigger(e.target.closest('.gro-lightbox-trigger')), { passive: true });

    container.addEventListener('click', e => {
      if (!settings.showLightbox) return;
      const trigger = e.target.closest('.gro-lightbox-trigger');
      if (!trigger) return;
      const before = trigger.dataset.before;
      const after = trigger.dataset.after;
      const smallBefore = trigger.dataset.smallBefore;
      const smallAfter = trigger.dataset.smallAfter;
      const start = parseInt(trigger.dataset.start, 10) || 0;
      const images = [];
      if (before) images.push({ src: before, smallSrc: smallBefore || before, label: 'BEFORE' });
      if (after) images.push({ src: after, smallSrc: smallAfter || after, label: 'AFTER' });
      if (images.length) openLightbox(images, start);
    });
  }

  // --- Init ---
  if (settings.enableFilters) generateDoctorFilters();
  initLightbox();
  render();
})();
